import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { TTypeOfEditRestaurantModalWindow } from '../../../../../../../../../editRestaurantState/editRestaurantContext';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { getActualFilePath, postMediaFile } from 'backend/additionalInformation';
import { createAppError } from 'utils/helpers';
import { openBrowseFile } from 'utils/openBrowseFile';
import Button from 'components/Button/Button';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import Text from 'components/Text/Text';

const CHARITY_LOGO_EXTENSION = 'png';
const CHARITY_LOGO_MAX_SIZE_BYTES = 1000 * 1000;

interface IProps {
	setCharityLogoFile: (fileId: Nullable<string>) => void;
	changeTypeOfModalWindow: (value: TTypeOfEditRestaurantModalWindow) => void;
	charityLogoFileId: Nullable<string>;
	charityLogoPath: Nullable<string>;
	isCharityEnabled: boolean;
}

export const CharityLogoUploader: React.FC<IProps> = React.memo(
	({ setCharityLogoFile, changeTypeOfModalWindow, charityLogoFileId, charityLogoPath, isCharityEnabled }) => {
		const { addAppError, showLoader, hideLoader } = useContext<IAppContext>(AppContext);
		const [uploadedLogoSrc, setUploadedLogoSrc] = useState<Nullable<string>>(null);
		const [savedLogoSrc, setSavedLogoSrc] = useState<Nullable<string>>(null);
		const [isSavedLogoLoading, setIsSavedLogoLoading] = useState(false);
		const [hasLogoLoadingError, setHasLogoLoadingError] = useState(false);
		const isNewLogoUploaded = Boolean(charityLogoFileId);
		const logoSrc = isNewLogoUploaded ? uploadedLogoSrc : savedLogoSrc;
		const hasLogo = isNewLogoUploaded || Boolean(charityLogoPath);

		useEffect(() => {
			setSavedLogoSrc(null);
			setHasLogoLoadingError(false);
			setIsSavedLogoLoading(false);

			if (!charityLogoPath) {
				return undefined;
			}

			let isActualRequest = true;

			const loadSavedLogo = async (filePath: string) => {
				try {
					setIsSavedLogoLoading(true);
					const { data } = await getActualFilePath(filePath);
					if (isActualRequest) {
						setSavedLogoSrc(data.preSignedUrl);
					}
				} catch {
					if (isActualRequest) {
						setHasLogoLoadingError(true);
					}
				} finally {
					if (isActualRequest) {
						setIsSavedLogoLoading(false);
					}
				}
			};

			loadSavedLogo(charityLogoPath);

			return () => {
				isActualRequest = false;
			};
		}, [charityLogoPath]);

		useEffect(() => {
			return () => {
				if (uploadedLogoSrc) {
					URL.revokeObjectURL(uploadedLogoSrc);
				}
			};
		}, [uploadedLogoSrc]);

		const uploadLogo = useCallback(
			async (files: FileList | null) => {
				const file = files?.[0];

				if (!file) {
					return;
				}

				if (file.name.split('.').pop()?.toLowerCase() !== CHARITY_LOGO_EXTENSION) {
					changeTypeOfModalWindow('charityLogoFileTypeError');
					return;
				}

				if (file.size > CHARITY_LOGO_MAX_SIZE_BYTES) {
					changeTypeOfModalWindow('charityLogoFileSizeExceeded');
					return;
				}

				const formData = new FormData();
				formData.append('file', file, file.name);

				try {
					showLoader();
					const { data } = await postMediaFile({ data: formData });
					setUploadedLogoSrc(URL.createObjectURL(file));
					setCharityLogoFile(data.fileId);
				} catch (err) {
					addAppError(createAppError(err instanceof Error ? err : new Error(String(err))));
				} finally {
					hideLoader();
				}
			},
			[changeTypeOfModalWindow, setCharityLogoFile, addAppError, showLoader, hideLoader],
		);

		const handleUploadClick = useCallback(() => {
			openBrowseFile({ onChoose: uploadLogo, accept: `.${CHARITY_LOGO_EXTENSION}` });
		}, [uploadLogo]);

		const handleDeleteClick = useCallback(() => {
			setCharityLogoFile(null);
		}, [setCharityLogoFile]);

		const handleLogoError = useCallback(() => {
			setHasLogoLoadingError(true);
		}, []);

		const renderLogo = () => {
			if (!isNewLogoUploaded && isSavedLogoLoading) {
				return (
					<div className="h-40 mb-2.5 flex items-center justify-center">
						<LoaderWithProps loaderSize="big" />
					</div>
				);
			}

			if (!isNewLogoUploaded && hasLogoLoadingError) {
				return (
					<div className="h-40 mb-2.5 flex items-center justify-center bg-purple-01-new">
						<svg className="w-1/2 h-1/2 fill-purple-300">
							<use xlinkHref={`${breadcrumbs}#brokenPicture`} />
						</svg>
					</div>
				);
			}

			if (!logoSrc) {
				return (
					<div className="h-40 mb-2.5 flex items-center justify-center bg-purple-01-new">
						<svg className="w-1/2 h-1/2 fill-purple-300">
							<use xlinkHref={`${breadcrumbs}#twoHills`} />
						</svg>
					</div>
				);
			}

			return (
				<img
					className="w-full h-40 mb-2.5 object-contain"
					src={logoSrc}
					alt="Charity logo"
					onError={handleLogoError}
				/>
			);
		};

		return (
			<div className="flex flex-1 flex-col" data-test="CharityLogoUploader">
				<Text
					className="mb-1"
					translationNameSpace="form"
					translationKey="charityLogoLabel"
					size="xs"
					theme="neutralLight"
					display="inline"
				/>
				{renderLogo()}
				<div className="flex justify-between">
					<Button
						icon={
							<svg className="w-6 h-6 mr-3 fill-white-00-new">
								<use xlinkHref={`${breadcrumbs}#${hasLogo ? 'reload' : 'uploadPhoto'}`} />
							</svg>
						}
						disabled={!isCharityEnabled}
						onClick={handleUploadClick}
						data-test="charityLogoUploadButton">
						<Text
							translationNameSpace="form"
							translationKey={hasLogo ? 'replaceButton' : 'uploadButton'}
							asFragment={true}
						/>
					</Button>
					{hasLogo && (
						<Button
							className="group text-purple-600"
							theme="secondary"
							icon={
								<svg className="w-6 h-6 mr-3 fill-purple-600 transition-all duration-300 group-hover:fill-white-00-new group-disabled:fill-purple-600">
									<use xlinkHref={`${breadcrumbs}#bin`} />
								</svg>
							}
							disabled={!isCharityEnabled}
							onClick={handleDeleteClick}
							data-test="charityLogoDeleteButton">
							<Text translationNameSpace="form" translationKey="deleteButton" asFragment={true} />
						</Button>
					)}
				</div>
			</div>
		);
	},
);
